import CompanyInfoBanner from "@/components/common/CompanyInfoBanner";
import { HEADER_HEIGHT } from "@/constants/components";
import { ParlorDetailProps } from "@/types/parlor";
import { markCostFunnelLogin } from "@/utils/costFunnelLoginContext";
import { toParlorDisplayName } from "@/utils/parlorName";
import { Box } from "@chakra-ui/react";
import { colors, customEvent } from "goi_common";
import { BaseStack } from "goi_common/src/design-system";
import { useRouter } from "next/router";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import { COST_FUNNEL_INFO_STEP_COUNT, CostFunnelStep } from "@/constants/parlor/costLeadFunnel";

import { allowIntentionalLeave } from "../hooks/useParlorRouterChangeLoad";
import CostFunnelStepHeader from "./components/CostFunnelStepHeader";
import Step1CostStructure from "./steps/Step1CostStructure";
import Step2GuestCostDiff from "./steps/Step2GuestCostDiff";
import Step3VenueSize from "./steps/Step3VenueSize";
import Step4FoodCost from "./steps/Step4FoodCost";

/**
 * 콘텐츠 좌우 패딩(px).
 *
 * 주황 배너는 이 값만큼 음수 마진으로 빼내 화면 좌우에 딱 붙인다(첫 스텝). 뒤 스텝의 배너는
 * 애초에 패딩 밖(퍼널 루트)에 있어 그대로 꽉 찬다. 두 곳이 같은 값을 봐야 어긋나지 않는다.
 */
const CONTENT_PADDING_X = 20;

/** 기존 견적 폼이 쓰는 것과 동일한 로그인 진입 URL. */
const LOGIN_URL = `/login?redirectTo=${encodeURIComponent(
  "/match/request/",
)}&event_from=uv_parlor_mtch_rqst`;

/**
 * 하단 sticky CTA 의 배경.
 *
 * 위쪽으로 갈수록 투명해지는 띠를 둬서, CTA 아래로 콘텐츠가 **살짝 비쳐 보이게** 한다.
 * 화면이 큰 기기에서는 첫 화면에 콘텐츠가 다 들어와 스크롤할 게 남았다는 신호가 없는데,
 * 이 페이드로 "아래에 더 있다"를 간접적으로 알린다. 흰 면이 바로 시작하면 콘텐츠가
 * 칼로 잘린 것처럼 끝나 보여 그 신호가 사라진다.
 *
 * `paddingTop` 이 페이드 띠의 높이고, 흰색 스톱(52%)이 그 띠가 어디서 시작하는지를 정한다.
 * 둘을 함께 움직여야 비율이 유지된다 - paddingTop 만 키우면 컨테이너가 커지는 만큼
 * 흰 면도 같이 커져서 실제 페이드 높이는 거의 안 변한다.
 */
const STICKY_CTA_FADE = {
  padding: "34px 20px 20px",
  background:
    "linear-gradient(to top, #FEFEFF 52%, rgba(254, 254, 255, 0.72) 80%, rgba(254, 254, 255, 0))",
} as const;

/**
 * 장례식장 비용 안내 리드 퍼널 (design_handoff_funeral_cost_lead_funnel).
 *
 * `/match/parlor-requests/[id]` 실험 B안. 이 페이지가 맡는 것은 **정보 4스텝**이다.
 * 마지막 "다음"을 누르면 기존 폼이 제출 후 넘기는 것과 같은 URL(`/login?redirectTo=...`)로
 * 이동하고, **제출 화면(조문객 수 + 현재 상황 + CTA 2개)은 그 URL 에서 까만 화면을 대체한다**
 * (`CostLeadSubmitLoginScreen`).
 *
 * 견적은 그 화면에서 만들어진다. 그래서 여기서는 API 를 부르지 않고, 제출 화면이 필요한
 * 장례식장 정보만 세션에 담아 넘긴다(`markCostFunnelLogin`).
 *
 * ## 배너와 스크롤
 * 주황 배너(`CompanyInfoBanner`)는 첫 스텝에서 H1 아래에 놓이고, 뒤 세 스텝에서는 페이지
 * **최상단**으로 올라간다. 그 스텝에 진입하면 sticky 스텝 헤더가 글로벌 헤더 바로 아래에
 * 붙는 위치까지 스크롤을 내려 배너가 딱 잘려 보이지 않게 한다. 위로 스크롤하면 곧바로
 * 배너가 드러나므로, 화면을 차지하지 않으면서 신뢰 신호는 남는다.
 */
export default function ParlorCostLeadFunnel({ parlor }: { parlor: ParlorDetailProps }) {
  const router = useRouter();

  const [step, setStep] = useState<CostFunnelStep>(0);

  const parlorName = useMemo(() => toParlorDisplayName(parlor.companyname), [parlor.companyname]);

  const isLastStep = step === COST_FUNNEL_INFO_STEP_COUNT - 1;

  /** 배너를 최상단에 두고 스크롤로 밀어내는 스텝 (첫 스텝 제외). */
  const hasTopBanner = step > 0;

  useEffect(() => {
    customEvent({ newGtm: { path: "parlor_cost", trigger_and_target: "uv" } });
  }, []);

  // 배너를 밀어낼 기준점. sticky 헤더 자체를 재는 대신 그 앞의 0높이 센티널을 재야 한다.
  // 이미 stuck 된 헤더의 rect 는 자연 위치가 아니라 붙어 있는 위치를 돌려주기 때문이다.
  const scrollAnchorRef = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    if (typeof window === "undefined") return;

    if (!hasTopBanner) {
      window.scrollTo({ top: 0, behavior: "auto" });
      return;
    }

    const alignBannerOutOfView = () => {
      const anchor = scrollAnchorRef.current;
      if (!anchor) return;
      const anchorTop = anchor.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({ top: Math.max(0, anchorTop - HEADER_HEIGHT), behavior: "auto" });
    };

    // 1차는 페인트 전에. rAF 로만 처리하면 배너가 한 프레임 보였다 사라져 깜박인다.
    alignBannerOutOfView();
    // 2차는 폰트·이미지로 배너 높이가 바뀐 경우 보정.
    const raf = window.requestAnimationFrame(alignBannerOutOfView);
    return () => window.cancelAnimationFrame(raf);
  }, [step, hasTopBanner]);

  const banner = (
    <CompanyInfoBanner
      onClick={() => {
        customEvent({
          newGtm: { path: "parlor_match_request", trigger_and_target: "uv_compnay_introduction" },
        });
        router.push("/funeral-prepare/company-introduction/");
      }}
    />
  );

  const goNext = useCallback(() => {
    customEvent({
      newGtm: { path: "parlor_cost", trigger_and_target: `step_next-${step + 1}` },
    });

    if (!isLastStep) {
      setStep((prev) => (prev + 1) as CostFunnelStep);
      return;
    }

    // 제출 화면이 필요한 값을 담아 넘긴다. 로그인 화면에서 parlors_detail(2.4MB)을 다시
    // 읽지 않기 위한 것으로, jangjiLoginContext 와 같은 방식이다.
    markCostFunnelLogin({
      parlorName,
      companyname: parlor.companyname,
      sido: Number(parlor.sido),
      sigungu: Number(parlor.sigungu),
      costRange: {
        min: Math.floor((parlor.expected_charge_sum?.["무빈소"] ?? 0) / 10000),
        max: Math.floor((parlor.expected_charge_sum?.["일반장"] ?? 0) / 10000),
      },
    });
    // 정상 전환이다. Next 가 클라이언트 라우팅에 실패해 풀 페이지 이동으로 폴백하면
    // beforeunload 확인창이 끼어들기 때문에(화이트리스트는 routeChangeStart 만 막는다)
    // 이동 직전에 방지를 해제한다.
    allowIntentionalLeave();
    router.push(LOGIN_URL);
  }, [step, isLastStep, parlor, parlorName, router]);

  const goPrev = useCallback(() => {
    customEvent({
      newGtm: { path: "parlor_cost", trigger_and_target: `step_prev-${step + 1}` },
    });
    setStep((prev) => Math.max(0, prev - 1) as CostFunnelStep);
  }, [step]);

  return (
    // wordBreak 는 상속되므로 최상위에 한 번만 걸면 자식 전체에 전파된다.
    // 모바일 좁은 화면에서 한글이 글자 한가운데서 끊기는 것을 막는다.
    <BaseStack width="100%" wordBreak="keep-all">
      {hasTopBanner && (
        <>
          {banner}
          <div ref={scrollAnchorRef} />
        </>
      )}

      <CostFunnelStepHeader parlorName={parlorName} stepIndex={step} topOffset={HEADER_HEIGHT} />

      <BaseStack
        width="100%"
        px={CONTENT_PADDING_X}
        pt={28}
        pb={24}
        minH={`calc(100vh - ${HEADER_HEIGHT}px)`}
      >
        {step === 0 && (
          <Step1CostStructure
            parlorName={parlorName}
            banner={banner}
            contentPaddingX={CONTENT_PADDING_X}
          />
        )}
        {step === 1 && <Step2GuestCostDiff />}
        {step === 2 && <Step3VenueSize />}
        {step === 3 && <Step4FoodCost />}
      </BaseStack>

      <Box
        position="sticky"
        bottom={0}
        width="100%"
        padding={STICKY_CTA_FADE.padding}
        background={STICKY_CTA_FADE.background}
      >
        <BaseStack direction="horizontal" width="100%" spacing={10}>
          {step > 0 && (
            <Box
              as="button"
              type="button"
              flexShrink={0}
              cursor="pointer"
              padding="16px 20px"
              borderRadius="12px"
              border={`1.5px solid ${colors.gray300}`}
              background="transparent"
              color={colors.gray800}
              className="body_16_m"
              onClick={goPrev}
            >
              이전
            </Box>
          )}
          <Box
            as="button"
            type="button"
            flex={1}
            cursor="pointer"
            padding="16px 20px"
            borderRadius="12px"
            background={colors.orange650}
            color={colors.white}
            className="subtitle_16_b"
            transition="background 160ms cubic-bezier(0.22, 1, 0.36, 1)"
            _hover={{ background: colors.orange700 }}
            _active={{ transform: "scale(0.985)" }}
            onClick={goNext}
          >
            다음
          </Box>
        </BaseStack>
      </Box>
    </BaseStack>
  );
}
